import "admin/src/style/videoMetadataPage.css";
import client from "libs/client";
import { useEffect, useId, useRef, useState } from "react";
import { useToast } from "ui-kit";
import { VIDEO_NAME_MAX_LENGTH } from "./functions";
import { getVideoUploadErrorMessage } from "./videoUploadErrors";

interface RenameModalProps {
	videoId: number;
	currentName: string;
	close: () => void;
}

export function RenameModal({ videoId, currentName, close }: RenameModalProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [name, setName] = useState(currentName);
	const inputId = useId();
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, []);

	const renameMutation = client.renameVideo.useMutation({
		onSuccess: () => {
			addSuccessToast(`Video ${videoId} name saved.`);
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("metadataVideos") || query.queryKey.includes("gameData"),
			});
			close();
		},
		// The API returns the video service's `{ message }` body as the error text, so unwrap it to
		// show why the rename was rejected instead of a raw payload.
		onError: (res) => addErrorToast(getVideoUploadErrorMessage(res)),
	});

	const trimmedName = name.trim();
	const canSave = trimmedName !== "" && trimmedName !== currentName && renameMutation.isLoading === false;

	return (
		<form
			className="renameVideoModal-container"
			onSubmit={(event) => {
				event.preventDefault();
				if (canSave === false) {
					return;
				}
				renameMutation.mutate({ id: videoId, body: { name: trimmedName } });
			}}
		>
			<label className="renameVideoModal-label" htmlFor={inputId}>
				Name
			</label>
			<input
				ref={inputRef}
				id={inputId}
				className="renameVideoModal-input"
				type="text"
				value={name}
				maxLength={VIDEO_NAME_MAX_LENGTH}
				onChange={(event) => setName(event.target.value)}
			/>
			<div className="buttons">
				<button className="submit" type="submit" disabled={canSave === false}>
					Save
				</button>
				<button className="cancel" type="button" onClick={() => close()}>
					Cancel
				</button>
			</div>
		</form>
	);
}
